import { useEffect, useRef } from 'react'
import { Button, Heading } from '../ui/index.js'

/**
 * Every create and edit in the app goes through this. Built once, on purpose:
 * docs/runbook/04-design-system.md §4 records that Jericho's took two bug reports to get
 * focus management and the submit-disabled state right.
 */
export function FormModal({
  title,
  dialogLabel,
  isOpen,
  onClose,
  onSubmit,
  submitLabel,
  canSubmit = true,
  busy = false,
  children,
}: {
  title: string
  dialogLabel?: string
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  submitLabel: string
  canSubmit?: boolean
  busy?: boolean
  children: React.ReactNode
}) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const restoreFocusTo = useRef<Element | null>(null)

  useEffect(() => {
    if (!isOpen) return
    restoreFocusTo.current = document.activeElement

    const focusable = dialogRef.current?.querySelector<HTMLElement>(
      'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
    )
    focusable?.focus()

    const focusables = () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'input, textarea, select, button, [href], [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => !element.hasAttribute('disabled') && element.tabIndex >= 0)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }

      if (event.key === 'Tab') {
        const items = focusables()
        if (items.length === 0) return

        const current = document.activeElement
        const currentIndex = items.indexOf(current as HTMLElement)
        const nextIndex = event.shiftKey
          ? currentIndex <= 0
            ? items.length - 1
            : currentIndex - 1
          : currentIndex === -1 || currentIndex === items.length - 1
            ? 0
            : currentIndex + 1

        event.preventDefault()
        items[nextIndex]?.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Returning focus matters for keyboard users: without it focus falls to the body
      // and the next Tab starts from the top of the page.
      ;(restoreFocusTo.current as HTMLElement | null)?.focus?.()
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(31, 26, 28, 0.4)',
        display: 'grid',
        placeItems: 'center',
        padding: 'var(--o-space-4)',
        zIndex: 50,
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel ?? title}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-xl)',
          boxShadow: 'var(--shadow-lg)',
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 'var(--o-space-6)',
        }}
      >
        <div className="row-between" style={{ marginBottom: 'var(--o-space-5)' }}>
          <Heading type="h3">{title}</Heading>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (!canSubmit || busy) return
            onSubmit()
          }}
        >
          {children}

          <div
            className="row-actions"
            style={{ justifyContent: 'flex-end', marginTop: 'var(--o-space-6)' }}
          >
            <Button kind="tertiary" type="button" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button kind="primary" type="submit" disabled={!canSubmit || busy}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
